import React, {MouseEvent, useState, ChangeEvent} from 'react';
import Menu from '@material-ui/core/Menu';
import Fade from '@material-ui/core/Fade';
import {
  Box,
  Checkbox,
  FormGroup,
  IconButton,
  Link,
  TextFieldProps,
  Toolbar,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import {THUMBNAIL_FILTER} from './const';
import {useIntl} from 'react-intl';
import {StudioSearch} from '../../../base-components/StudioSearch';
import SearchIcon from '../../../assets/icons/SearchIcon';
import './ImageListFilter.scss';

export type SortId = 'recentlyAddedAsc' | 'recentlyAddedDesc' | 'recentlyAdded';
export type ImageStateId = 'reviewed' | 'notReviewed';
export type ImageLabelStateId = 'labeled' | 'notLabeled';
export type FilterTypeId = 'checkbox' | 'radio';

export type FilterItem = {
  label: string;
  value: string;
};

export type FilterProps = {
  groupId: string;
  id: string;
  value: string | string[];
};

export type FilterItemGroup = {
  id: string;
  label: string;
  type: FilterTypeId;
  items: FilterItem[];
};

export default function ImageListFilter({
  sortBy = 'recentlyAddedAsc',
  iconSize = 'small',
  disabled = false,
  imageStateBy = [],
  imageLabelStateBy = [],
  onChange = () => {},
  onReset = () => {},
}: {
  sortBy: SortId;
  imageStateBy?: ImageStateId[];
  imageLabelStateBy?: ImageLabelStateId[];
  iconSize?: 'small' | 'medium';
  disabled?: boolean;
  onChange?: (args: FilterProps) => void;
  onReset?: () => void;
}) {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState<null | any>(null);
  const open = Boolean(anchorEl);
  const handleClick = (e: MouseEvent) => setAnchorEl(e?.currentTarget);
  const onClick = () => setAnchorEl(null);

  const onRadioFilterSelect = (groupId: string, value: string) => {
    onChange({groupId, id: '', value});
  };

  const onCheckBoxSelect = (groupId: string, id: string, checked: boolean) => {
    onChange({
      groupId,
      id,
      value: checked
        ? imageLabelStateBy.concat([id as ImageLabelStateId])
        : imageLabelStateBy.filter(i => i !== id),
    });
  };

  const getValue = (filter: FilterItemGroup) => {
    switch (filter.id) {
      case 'sortBy':
        return sortBy;
    }
  };
  return (
    <>
      <IconButton size={iconSize} onClick={handleClick} disabled={disabled}>
        <FilterListIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClick}
        TransitionComponent={Fade}
        getContentAnchorEl={null}
        className="detection-view__thumbnail_filter_menu"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Toolbar className="detection-view__thumbnail_toolbar" variant="dense">
          <h6>{intl.formatMessage({id: 'general.filters'})}</h6>
          <Box flexGrow={1}></Box>
          <Link component="button" variant="body2" onClick={onReset}>
            {intl.formatMessage({id: 'general.filter.clear'})}
          </Link>
        </Toolbar>
        {THUMBNAIL_FILTER.map((filter, i) => {
          return filter.type === 'checkbox' ? (
            <CheckboxList
              key={i}
              group={filter as FilterItemGroup}
              value={imageLabelStateBy}
              onSelect={onCheckBoxSelect}
            />
          ) : (
            <RadioList
              key={i}
              group={filter as FilterItemGroup}
              value={getValue(filter as FilterItemGroup)}
              onSelect={onRadioFilterSelect}
            />
          );
        })}
      </Menu>
    </>
  );
}

export const RadioList = ({
  group,
  value = '',
  onSelect = (group: string, value: string) => {},
}: {
  group: FilterItemGroup;
  value?: string;
  onSelect: (group: string, value: string) => void;
}) => {
  const intl = useIntl();
  const onChange = (e: ChangeEvent<HTMLInputElement>) =>
    onSelect(group.id, (e.target as HTMLInputElement).value);
  return (
    <FormControl component="fieldset">
      <FormLabel component="legend">{intl.formatMessage({id: group.label})}</FormLabel>
      <RadioGroup name={group.id} value={value} onChange={onChange}>
        {group.items.map((item: FilterItem, i) => (
          <FormControlLabel
            key={i}
            control={<Radio color="primary" />}
            {...item}
            label={intl.formatMessage({id: item.label})}
          />
        ))}
      </RadioGroup>
    </FormControl>
  );
};

export const CheckboxList = ({
  group,
  value = [],
  onSelect = (group: string, id: string, value: boolean) => {},
}: {
  group: FilterItemGroup;
  value?: string[];
  onSelect: (group: string, id: string, value: boolean) => void;
}) => {
  const intl = useIntl();
  const onChange = (id: string, value: boolean) => onSelect(group.id, id, value);

  return (
    <FormControl component="fieldset">
      <FormLabel component="legend">{intl.formatMessage({id: group.label})}</FormLabel>
      <FormGroup>
        {group.items.map((item: FilterItem, i) => (
          <FormControlLabel
            key={i}
            control={
              <Checkbox
                checked={value.includes(item.value)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  onChange(item.value, (e.target as HTMLInputElement).checked as boolean)
                }
                color="primary"
              />
            }
            label={intl.formatMessage({id: item.label})}
          />
        ))}
      </FormGroup>
    </FormControl>
  );
};

export const SearchField = (props: {
  value: string;
  onChange: TextFieldProps['onChange'];
}) => {
  const [isSearch, setIsSearch] = useState(false);
  const intl = useIntl();

  return (
    <div className="detection-view__thumbnail_search_input">
      {isSearch ? (
        <StudioSearch
          autoFocus
          size="small"
          placeholder={intl.formatMessage({id: 'form.search'})}
          onChange={props.onChange}
          value={props.value}
          onBlur={() => {
            !props.value.trim() && setIsSearch(false);
          }}
        />
      ) : (
        <IconButton size="small" onClick={e => setIsSearch(true)}>
          <SearchIcon />
        </IconButton>
      )}
    </div>
  );
};

export const getFilterRSQL = (args: FilterProps) => {
  const {groupId, value} = args;
  switch (groupId) {
    case 'imageLabelStateBy':
      if (value.length === 0 || value.length === 2) {
        return undefined;
      } else {
        return `category=ex=${value[0] === 'labeled' ? 'true' : 'false'}`;
      }
    case 'split':
      if (value.length === 0 || value.length === 2) {
        return undefined;
      } else {
        return `split==${value}`;
      }
  }
  return undefined;
};
